import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Clock, ChefHat, Bike, PackageCheck, Package, MapPin, Phone, Wifi, WifiOff, Navigation, Gift, KeyRound, XCircle } from "lucide-react";
import { apiClient, API } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { formatGNF, STATUS_LABEL, PAYMENT_LABEL } from "@/utils/format";
import { fmtReopen } from "@/utils/reopen";
import ReviewForm from "@/components/ReviewForm";
import DeliveryMap from "@/components/DeliveryMap";
import CancelOrderModal from "@/components/CancelOrderModal";
import { toast } from "sonner";
import { BrandLoader } from "@/components/BrandLoader";
import { BrandProgress } from "@/components/BrandProgress";

// Approximate Conakry zone centroids (for delivery map fallback when no GPS)
const ZONE_COORDS = {
  Kaloum: [9.5092, -13.7122],
  Dixinn: [9.5400, -13.6800],
  Matam: [9.5500, -13.6800],
  Ratoma: [9.5876, -13.6671],
  Matoto: [9.5667, -13.6000],
  Nongo: [9.6300, -13.6100],
  Kipé: [9.6080, -13.6600],
  Lambanyi: [9.6300, -13.5900],
  Taouyah: [9.5900, -13.6700],
  Sonfonia: [9.6450, -13.5900],
  Cosa: [9.5700, -13.6700],
};

const CANCEL_REASON_KEYS = ["rupture", "client_injoignable", "adresse_introuvable", "erreur_commande", "changement_avis", "delai_trop_long", "imprevu_livraison", "autre"];

// Distance haversine (km) + ETA scooter ~22 km/h — mêmes hypothèses que côté livreur
const toKm = (a, b) => {
  const R = 6371, dLat = (b[0] - a[0]) * Math.PI / 180, dLng = (b[1] - a[1]) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * Math.PI / 180) * Math.cos(b[0] * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};
const fmtKm = (km) => (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`);
const etaMin = (km) => Math.max(2, Math.round((km / 22) * 60));

// Petit carillon (WebAudio) joué quand le livreur est tout proche
const playNearChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1174.66].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = f;
      o.type = "sine";
      o.connect(g);
      g.connect(ctx.destination);
      const t0 = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.25, t0 + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
      o.start(t0);
      o.stop(t0 + 0.4);
    });
  } catch (e) { /* audio bloqué sans interaction utilisateur */ }
};

// Support NelDaari (WhatsApp) — annulations après acceptation
const SUPPORT_WHATSAPP = "33758262447";

const STEPS = [
  { key: "received", icon: CheckCircle2 },
  { key: "preparing", icon: ChefHat },
  { key: "ready", icon: Package },
  { key: "delivering", icon: Bike },
  { key: "delivered", icon: PackageCheck },
];

export default function OrderTracking() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [params] = useSearchParams();
  const { clear: clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [review, setReview] = useState(null);
  const [sms, setSms] = useState([]);
  const [wsState, setWsState] = useState("connecting"); // connecting|open|closed
  const [driverLoc, setDriverLoc] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const wsRef = useRef(null);

  // Proche arrivée : son + bannière quand le livreur est à moins de 500 m (une seule fois)
  const [nearArrival, setNearArrival] = useState(false);
  const nearNotifiedRef = useRef(false);
  useEffect(() => {
    if (!order || !driverLoc || order.status !== "delivering" || nearNotifiedRef.current) return;
    const dest = order.client_gps?.lat != null ? [order.client_gps.lat, order.client_gps.lng] : ZONE_COORDS[order.zone];
    if (!dest || driverLoc.lat == null) return;
    if (toKm([driverLoc.lat, driverLoc.lng], dest) <= 0.5) {
      nearNotifiedRef.current = true;
      setNearArrival(true);
      playNearChime();
    }
  }, [driverLoc, order]);

  // Initial fetch + review check
  useEffect(() => {
    apiClient.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => {});
    apiClient.get(`/orders/${id}/review`).then(({ data }) => setReview(data)).catch(() => {});
  }, [id]);

  // Stripe return handler — le panier n'est vidé qu'après confirmation réelle du paiement
  useEffect(() => {
    const stripeParam = params.get("stripe");
    if (stripeParam === "success" && order?.stripe_session_id) {
      apiClient.get(`/payments/stripe/status/${order.stripe_session_id}`)
        .then(({ data }) => {
          if (data.payment_status === "paid") {
            toast.success(t("tracking.cardConfirmed"));
            clearCart();
            apiClient.get(`/orders/${id}`).then(({ data: o }) => setOrder(o));
          } else {
            toast.warning(t("tracking.cardVerifying"));
          }
        })
        .catch(() => {});
    } else if (stripeParam === "cancel") {
      toast.warning(t("tracking.cardCancelled"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, order?.stripe_session_id, id]);

  // App mobile : Stripe ouvert dans le navigateur intégré → polling du statut,
  // fermeture automatique du navigateur dès confirmation (pas de deep link).
  const stripePending = params.get("stripe") === "pending" && order?.payment_status !== "paid";
  useEffect(() => {
    if (params.get("stripe") !== "pending" || !order?.stripe_session_id) return;
    if (order?.payment_status === "paid") return;
    let stopped = false;
    const startedAt = Date.now();
    const tick = async () => {
      if (stopped) return;
      try {
        const { data } = await apiClient.get(`/payments/stripe/status/${order.stripe_session_id}`);
        if (data.payment_status === "paid") {
          try {
            const { Browser } = await import("@capacitor/browser");
            await Browser.close();
          } catch (e) { void e; }
          toast.success(t("tracking.cardConfirmed"));
          clearCart();
          apiClient.get(`/orders/${id}`).then(({ data: o }) => setOrder(o));
          return;
        }
      } catch (e) { void e; }
      if (Date.now() - startedAt < 5 * 60 * 1000) setTimeout(tick, 3000);
    };
    tick();
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, order?.stripe_session_id, order?.payment_status, id]);

  // WebSocket subscription
  useEffect(() => {
    if (!id) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace(/^https?/, backendUrl.startsWith("https") ? "wss" : "ws") + `/api/ws/orders/${id}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => setWsState("open");
    ws.onclose = () => setWsState("closed");
    ws.onerror = () => setWsState("closed");
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.order) {
          setOrder(msg.order);
          if (msg.order.driver_location) setDriverLoc(msg.order.driver_location);
        }
        if (msg.type === "driver.location" && msg.location) {
          setDriverLoc(msg.location);
        }
        if (msg.type === "order.status") {
          const driver = msg.order?.driver_name;
          const eta = msg.order?.ready_estimate_at
            ? new Date(msg.order.ready_estimate_at).toLocaleTimeString(i18n.language === "en" ? "en-GB" : "fr-FR", { hour: "2-digit", minute: "2-digit" })
            : null;
          const STATUS_TOAST = {
            received: t("tracking.toastReceived"),
            preparing: eta ? t("tracking.toastPreparingEta", { eta }) : t("tracking.toastPreparing"),
            ready: t("tracking.toastReady"),
            delivering: driver ? t("tracking.toastDeliveringNamed", { driver }) : t("tracking.toastDelivering"),
            delivered: t("tracking.toastDelivered"),
            cancelled: t("tracking.toastCancelled"),
            incident: t("tracking.toastIncident"),
          };
          toast.info(STATUS_TOAST[msg.status] || t("tracking.toastUpdate", { status: t(`status.${msg.status}`, msg.status) }));
        }
      } catch {}
    };
    return () => { try { ws.close(); } catch {} };
  }, [id]);

  const activeIndex = useMemo(() => order ? STEPS.findIndex((s) => s.key === order.status) : -1, [order]);

  if (!order) return <BrandLoader message={t("tracking.loading")} />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-red">{t("tracking.orderLabel")}</div>
          <h1 className="font-display text-3xl font-semibold">#{order.order_id.slice(-6).toUpperCase()}</h1>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="ws-state" className={`inline-flex items-center gap-1 text-xs rounded-full px-3 py-1 ${wsState === "open" ? "bg-[#E7F6EE] text-brand-green" : "bg-elevated text-mute"}`}>
            {wsState === "open" ? <><Wifi className="w-3 h-3" /> {t("tracking.realtime")}</> : <><WifiOff className="w-3 h-3" /> {t("tracking.offline")}</>}
          </span>
          <span className="rounded-full bg-elevated px-4 py-2 text-sm font-semibold">{t(`status.${order.status}`, STATUS_LABEL[order.status])}</span>
        </div>
      </div>
      <p className="text-mute mb-8">{t("tracking.at")} <span className="text-ink font-semibold">{order.restaurant_name}</span></p>

      {/* Commande programmée pendant la fermeture du restaurant */}
      {order.is_scheduled && order.status === "received" && (
        <div className="mb-6 rounded-2xl border border-[#E67E22]/30 bg-[#E67E22]/10 px-4 py-3 flex items-center gap-3" data-testid="scheduled-banner">
          <Clock className="w-5 h-5 text-[#B35C00] shrink-0" />
          <div className="text-sm">{t("tracking.scheduledBanner", { when: fmtReopen(order.scheduled_for, i18n.language) })}</div>
        </div>
      )}

      {/* Bannière proche arrivée (livreur < 500 m) */}
      {nearArrival && order.status === "delivering" && (
        <div className="mb-6 rounded-2xl border border-brand-green/30 bg-brand-green/10 px-4 py-3 flex items-center gap-3" data-testid="near-arrival-banner">
          <Bike className="w-6 h-6 text-brand-green shrink-0 animate-bounce" />
          <div>
            <div className="font-semibold text-sm">{t("tracking.nearTitle")}</div>
            <div className="text-xs text-mute">{t("tracking.nearBody")}</div>
          </div>
        </div>
      )}

      {/* Progression de la commande — le N NelDaari se remplit étape par étape */}
      <BrandProgress status={order.status} />

      {/* DELIVERY CODE — always visible to client while order is in flight */}
      {order.delivery_code && !["delivered", "cancelled"].includes(order.status) && (
        <div className="bg-gradient-to-br from-brand-yellow/30 to-brand-red/10 border-2 border-brand-yellow rounded-2xl p-5 mb-6 flex items-center gap-4" data-testid="delivery-code-card">
          <div className="w-14 h-14 rounded-2xl bg-brand-yellow text-ink grid place-items-center shrink-0">
            <KeyRound className="w-7 h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-red font-bold">{t("tracking.deliveryCode")}</div>
            <div className="font-display text-4xl font-bold tracking-[0.4em] text-ink" data-testid="delivery-code-value">{order.delivery_code}</div>
            <div className="text-xs text-mute mt-1">{t("tracking.deliveryCodeHint")}</div>
          </div>
        </div>
      )}

      {/* Third-party (diaspora) order: shareable tracking link for the recipient */}
      {order.is_gift && order.share_token && !["cancelled"].includes(order.status) && (
        <div className="bg-white border-2 border-[#B8CCEC] rounded-2xl p-5 mb-6" data-testid="gift-share-card">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#003087] font-bold">🎁 {t("tracking.giftFor", { name: order.gift_recipient_name || t("tracking.aLovedOne") })}</div>
          <p className="text-sm text-mute mt-1">{t("tracking.giftShareHint")}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <a
              href={`https://wa.me/${(order.gift_recipient_phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Salut ${order.gift_recipient_name || ""} ! Je t'ai commandé un repas sur NelDaari 🎁 Suis la livraison et récupère ton code ici : ${window.location.origin}/track/${order.share_token}`)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="gift-share-whatsapp"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-2.5 text-sm font-semibold"
            >
              Envoyer par WhatsApp
            </a>
            <button
              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/track/${order.share_token}`).then(() => toast.success(t("tracking.linkCopied"))).catch(() => {}); }}
              data-testid="gift-share-copy"
              className="inline-flex items-center gap-2 rounded-full border border-sand hover:border-ink px-5 py-2.5 text-sm font-semibold"
            >
              {t("tracking.copyLink")}
            </button>
          </div>
        </div>
      )}

      {/* Cancellation banner */}
      {["cancelled", "cancelled_in_delivery"].includes(order.status) && order.cancellation && (
        <div className="bg-[#FFF5F6] border border-brand-red/30 rounded-2xl p-5 mb-6" data-testid="cancellation-banner">
          <div className="text-[10px] uppercase tracking-widest text-brand-red font-semibold">
            {order.status === "cancelled_in_delivery" ? t("status.cancelled_in_delivery") : t("tracking.cancelledTitle")}
          </div>
          <div className="font-semibold mt-0.5">{t("tracking.reason")} : {CANCEL_REASON_KEYS.includes(order.cancellation.reason) ? t(`tracking.cancelReasons.${order.cancellation.reason}`) : order.cancellation.reason}</div>
          {order.cancellation.comment && <div className="text-sm text-mute mt-1">« {order.cancellation.comment} »</div>}
          <div className="text-xs text-mute mt-1">{t("tracking.by")} {order.cancellation.by_role} · {new Date(order.cancellation.at).toLocaleString(i18n.language === "en" ? "en-GB" : "fr-FR")}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Paiement carte en cours (app mobile — navigateur intégré Stripe) */}
          {stripePending && (
            <div className="bg-white border border-sand rounded-2xl p-4 flex items-center gap-3" data-testid="stripe-pending-banner">
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-green opacity-60" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-brand-green" />
              </span>
              <div className="min-w-0">
                <div className="font-semibold text-sm">{t("tracking.cardPendingTitle")}</div>
                <div className="text-xs text-mute">{t("tracking.cardPendingHint")}</div>
              </div>
            </div>
          )}
          {/* Prep ETA banner while the restaurant is cooking */}
          {order.status === "preparing" && (
            <div className="bg-white border border-sand rounded-2xl p-4 flex items-center gap-3" data-testid="prep-eta-banner">
              <ChefHat className="w-5 h-5 text-[#E67E22] shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-sm">{t("tracking.prepTitle")}</div>
                <div className="text-xs text-mute">
                  {order.ready_estimate_at
                    ? t("tracking.prepEtaText", { eta: new Date(order.ready_estimate_at).toLocaleTimeString(i18n.language === "en" ? "en-GB" : "fr-FR", { hour: "2-digit", minute: "2-digit" }) })
                    : t("tracking.prepNoEta")}
                </div>
              </div>
            </div>
          )}
          {/* Driver search in progress — never show a driver name until one has accepted */}
          {order.status === "ready" && !order.driver_id && (
            <div className="bg-white border border-sand rounded-2xl p-4 flex items-center gap-3" data-testid="driver-search-banner">
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-red opacity-60" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-brand-red" />
              </span>
              <div className="min-w-0">
                <div className="font-semibold text-sm flex items-center gap-1.5">
                  <Bike className="w-4 h-4 text-brand-red" /> {t("tracking.driverSearch")}
                </div>
                <div className="text-xs text-mute">{t("tracking.driverSearchHint")}</div>
              </div>
            </div>
          )}
          <div className="bg-white border border-sand rounded-2xl p-6">
            <h2 className="font-display text-xl font-semibold mb-6">{t("tracking.timelineTitle")}</h2>
            <ol className="relative border-l-2 border-sand ml-3 space-y-6">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const done = i <= activeIndex && order.status !== "cancelled";
                const current = i === activeIndex && order.status !== "cancelled";
                return (
                  <li key={s.key} className="ml-6">
                    <span className={`absolute -left-[18px] grid place-items-center w-9 h-9 rounded-full ${done ? "bg-brand-green text-white" : "bg-white border-2 border-sand text-mute"}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className={`font-display text-lg ${current ? "text-brand-red font-semibold" : done ? "text-ink" : "text-mute"}`}>
                      {t(`tracking.steps.${s.key}`)}
                    </div>
                    {current && <div className="text-sm text-mute mt-1">{t("tracking.stepCurrent")}</div>}
                  </li>
                );
              })}
            </ol>
          </div>

          {/* DELIVERY MAP */}
          {(order.status === "delivering" || driverLoc) && (
            <div className="bg-white border border-sand rounded-2xl p-4">
              <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
                <Bike className="w-4 h-4 text-brand-red" /> Carte de livraison
              </h3>
              <DeliveryMap
                driverLoc={driverLoc}
                clientPos={ZONE_COORDS[order.zone]}
              />
              <p className="text-[10px] uppercase tracking-widest text-mute mt-2">
                {driverLoc ? t("tracking.driverLive") : t("tracking.zoneLabel", { zone: order.zone })}
              </p>
            </div>
          )}

          {/* Review when delivered */}
          {order.status === "delivered" && order.points_earned > 0 && (
            <div className="bg-gradient-to-br from-[#009E60] to-[#0F7A4D] text-white rounded-2xl p-5" data-testid="points-earned-banner">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 grid place-items-center"><Gift className="w-5 h-5" /></div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] opacity-80">{t("tracking.congrats")}</div>
                  <div className="font-display text-xl font-semibold">{t("tracking.pointsEarned", { n: order.points_earned })}</div>
                </div>
              </div>
            </div>
          )}
          {order.status === "delivered" && !review && (
            <ReviewForm orderId={order.order_id} onSubmitted={setReview} />
          )}
          {review && (
            <div className={`rounded-2xl p-6 border ${review.status === "pending" ? "bg-[#FFF9E6] border-[#F4E3AD]" : "bg-[#E7F6EE] border-[#C8EAD5]"}`} data-testid="my-review-card">
              <h3 className="font-display text-lg font-semibold mb-1">{t("tracking.reviewThanks", { rating: review.rating })}</h3>
              {review.comment && <p className="text-sm text-mute">"{review.comment}"</p>}
              {review.status === "pending" && (
                <p className="text-xs text-[#946A00] mt-2" data-testid="review-pending-note">{t("tracking.reviewPending")}</p>
              )}
            </div>
          )}
        </div>

        <aside className="space-y-4">
          {order.status === "delivering" && driverLoc && (
            <div className="bg-white border-2 border-brand-red rounded-2xl p-6 shadow-lg" data-testid="driver-live-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                  <Bike className="w-5 h-5 text-brand-red" /> {t("tracking.driverOnWay")}
                </h3>
                <span className="text-xs text-brand-green inline-flex items-center gap-1 font-semibold">
                  <span className="w-2 h-2 rounded-full bg-brand-green animate-pulse" /> Live
                </span>
              </div>
              <div className="text-sm space-y-1.5">
                <div className="font-semibold">{driverLoc.driver_name || t("tracking.driverFallback")}</div>
                {(() => {
                  const dest = order.client_gps?.lat != null ? [order.client_gps.lat, order.client_gps.lng] : ZONE_COORDS[order.zone];
                  if (!dest || driverLoc.lat == null) return null;
                  const km = toKm([driverLoc.lat, driverLoc.lng], dest);
                  return (
                    <div className="rounded-xl bg-brand-red/5 border border-brand-red/15 px-3 py-2 flex items-center justify-between gap-2" data-testid="driver-eta-card">
                      <span className="text-xs font-semibold text-ink inline-flex items-center gap-1.5">
                        <Navigation className="w-3.5 h-3.5 text-brand-red" /> {t("tracking.driverDistance", { dist: fmtKm(km) })}
                      </span>
                      <span className="text-xs font-bold text-brand-red shrink-0">{t("tracking.driverEta", { min: etaMin(km) })}</span>
                    </div>
                  );
                })()}
                <div className="text-mute text-xs flex items-center gap-1.5">
                  <Navigation className="w-3.5 h-3.5" />
                  {driverLoc.lat?.toFixed(5)}, {driverLoc.lng?.toFixed(5)}
                </div>
                <div className="text-mute text-xs">
                  {t("tracking.updatedAt")} {new Date(driverLoc.ts).toLocaleTimeString(i18n.language === "en" ? "en-GB" : "fr-FR")}
                </div>
                <a
                  href={`https://www.google.com/maps?q=${driverLoc.lat},${driverLoc.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="driver-live-maps-link"
                  className="mt-3 block w-full text-center rounded-xl border border-sand hover:border-brand-red py-2.5 text-sm font-semibold"
                >
                  {t("tracking.seeMap")}
                </a>
              </div>
            </div>
          )}

          <div className="bg-white border border-sand rounded-2xl p-6">
            <h3 className="font-display text-lg font-semibold mb-3">{t("tracking.deliveryTitle")}</h3>
            <div className="text-sm text-mute space-y-2">
              <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /> <span>{order.address}, {order.zone}</span></div>
              <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> {order.phone}</div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4" /> {t("tracking.estimated")}</div>
            </div>
            {order.client_gps?.lat != null && (
              <div className="mt-3" data-testid="tracking-gps-map">
                <DeliveryMap clientPos={[order.client_gps.lat, order.client_gps.lng]} height={176} />
                <div className="text-[11px] text-mute mt-1">{t("tracking.gpsShared")}</div>
              </div>
            )}
            {order.is_gift && (
              <div className="mt-3 rounded-xl border border-brand-yellow bg-brand-yellow/10 p-3 text-sm" data-testid="tracking-gift-info">
                <div className="font-semibold text-xs uppercase tracking-widest mb-1">🎁 {t("tracking.giftForLabel")}</div>
                <div className="font-semibold text-ink">{order.gift_recipient_name}</div>
                {order.gift_recipient_phone && <div className="text-mute text-xs">{order.gift_recipient_phone}</div>}
                {order.gift_message && <div className="text-xs text-mute mt-1 italic">« {order.gift_message} »</div>}
              </div>
            )}
          </div>
          <div className="bg-white border border-sand rounded-2xl p-6">
            <h3 className="font-display text-lg font-semibold mb-3">{t("tracking.summary")}</h3>
            <div className="text-sm space-y-2 mb-3">
              {order.items.map((it, idx) => (
                <div key={idx} className="flex justify-between gap-2">
                  <span className="min-w-0">
                    {it.quantity}× {it.name}
                    {it.options?.length > 0 && (
                      <span className="block text-xs text-mute" data-testid={`tracking-item-options-${idx}`}>
                        {it.options.map((o) => o.price_delta > 0 ? `${o.name} (+${formatGNF(o.price_delta)})` : o.name).join(" · ")}
                      </span>
                    )}
                    {it.note && <span className="block text-xs text-mute italic">« {it.note} »</span>}
                  </span>
                  <span className="whitespace-nowrap">{formatGNF(it.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-sand pt-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-mute">{t("cart.subtotal")}</span><span>{formatGNF(order.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-mute">{t("cart.delivery")}</span><span>{formatGNF(order.delivery_fee)}</span></div>
              <div className="flex justify-between font-display text-lg font-semibold mt-2 pt-2 border-t border-sand"><span>{t("cart.total")}</span><span>{formatGNF(order.total)}</span></div>
              <div className="flex justify-between text-xs text-mute pt-2"><span>{t("tracking.payment")}</span><span>{t(`pay.${order.payment_method}`, PAYMENT_LABEL[order.payment_method])}</span></div>
            </div>
          </div>
          <Link to="/profile" className="block text-center text-sm text-brand-red font-semibold">{t("tracking.viewHistory")}</Link>

          {/* Cancel button (client, only before restaurant accepts) */}
          {order.status === "received" && (
            <button
              onClick={() => setCancelOpen(true)}
              data-testid="cancel-order-btn"
              className="w-full rounded-full border border-brand-red text-brand-red hover:bg-brand-red hover:text-white transition-colors px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2"
            >
              <XCircle className="w-4 h-4" /> {t("tracking.cancelOrder")}
            </button>
          )}
          {/* Après acceptation : annulation uniquement via le support */}
          {["preparing", "ready", "delivering"].includes(order.status) && (
            <div data-testid="cancel-support-block">
              <a
                href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(
                  order.status === "delivering"
                    ? `Bonjour, ma commande #${order.order_id.slice(-6).toUpperCase()} (${order.restaurant_name}) est en cours de livraison et je souhaite une modification. Merci de m'aider.`
                    : `Bonjour, je souhaite annuler ma commande #${order.order_id.slice(-6).toUpperCase()} (${order.restaurant_name}). Elle est déjà en préparation. Merci de m'aider.`
                )}`}
                target="_blank"
                rel="noreferrer"
                data-testid="cancel-contact-support-btn"
                className="w-full rounded-full border border-sand text-mute hover:border-ink hover:text-ink transition-colors px-4 py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                <XCircle className="w-4 h-4" /> {order.status === "delivering" ? t("tracking.supportModifyBtn") : t("tracking.supportCancelBtn")}
              </a>
              <p className="text-xs text-mute text-center mt-2">
                {order.status === "delivering" ? t("tracking.supportDelivering") : t("tracking.supportPreparing")}
              </p>
            </div>
          )}
        </aside>
      </div>

      <CancelOrderModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        orderId={order.order_id}
        role="client"
        onCancelled={(o) => { setOrder(o); setCancelOpen(false); }}
      />
    </div>
  );
}
